import React, { useContext, useEffect, useState } from 'react';
import {
  FormWithRedirect,
  TextInput,
  SaveButton,
  BooleanInput,
} from 'react-admin';
import DialogActions from '@material-ui/core/DialogActions';
import Divider from '@material-ui/core/Divider';
import { Box, Link } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import BuildIcon from '@material-ui/icons/Build';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import NotesIcon from '@material-ui/icons/Notes';
import Typography from '@material-ui/core/Typography';
import moment from 'moment';
import LoopIcon from '@material-ui/icons/Loop';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import WarningOutlineIcon from '@material-ui/icons/ReportProblemOutlined';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import { QuickAppointmentContext } from '../Context';
import CompatibleDatesCalendar from './Calendar/CompatibleDatesCalendar';
import { convertTime, getZ3PConfigValue, areTimeSlotsEnabled } from '../helper';
import useStyles from './styles';
import RecurringPlanForm from '../RecurringAppointments/RecurringPlanForm';
import RecurringPlanSummary from '../RecurringAppointments/RecurringPlanSummary';
import dataProvider from '../dataProvider';
import { getVehicleType } from '../VehicleConstants';

const THIRTY_MINUTE_INTERVAL_IN_SECONDS = 30 * 60;

export const allTimes = () => {
  const startTimes = [];
  const minTime = getZ3PConfigValue('time_range_min');
  const maxTime = getZ3PConfigValue('time_range_max');
  for (let h = minTime; h <= maxTime; h += 1) {
    // We truncate the start hour since it could be (for example) `8.5` to mean 8:30am.
    // Even if it's 8:30am, we will include 8:00am and 8:15am in this list.
    let hour = Math.trunc(h);
    let ampm = 'am';
    if (h > 12) {
      hour -= 12;
      ampm = 'pm';
    } else if (h === 12) {
      ampm = 'pm';
    } else if (h === 0) {
      hour = 12;
    }
    startTimes.push(
      `${hour}:00 ${ampm}`,
      `${hour}:15 ${ampm}`,
      `${hour}:30 ${ampm}`,
      `${hour}:45 ${ampm}`,
    );
  }
  return startTimes;
};

const ScheduleForm = () => {
  const {
    handleNext,
    scheduleData,
    setScheduleData,
    addressData,
    serviceData,
    savedData,
    completedSteps,
    loadingDates,
    isRecurring,
    setIsRecurring,
    onRruleChange,
    rruleAttributes,
    rruleCombined,
    isEndDateInvalid,
    datesToCreate,
    setDatesToCreate,
    unavailableRecurringDates,
    recurringDatesWithConflicts,
    conflictReason,
    setConflictReason,
    isUsingOptimalSchedule,
    setIsUsingOptimalSchedule,
    isLoadingAdditionalDates,
    selectedTimeSlot,
    vehicleData,
  } = useContext(QuickAppointmentContext);

  const classes = useStyles();
  const theme = useTheme();

  const isOfferingRecurringAppointments = getZ3PConfigValue(
    'is_offering_recurring_appointments',
  );
  const serviceLocations = getZ3PConfigValue('service_locations');
  const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');
  const isUsingTimeSlots = areTimeSlotsEnabled();

  const defaultArrivalTime =
    scheduleData?.planned_arrival_time ||
    `${convertTime(getZ3PConfigValue('time_range_min'), 'militaryString')}`;
  const defaultDepartureTime =
    scheduleData?.planned_departure_time ||
    `${convertTime(getZ3PConfigValue('time_range_max'), 'militaryString')}`;

  const [arrivalTime, setArrivalTime] = useState(defaultArrivalTime);
  const [departureTime, setDepartureTime] = useState(defaultDepartureTime);
  const [serviceVehicleList, setServiceVehicleList] = useState([]);

  const client_location_id =
    savedData?.client_location_id || addressData?.client_location_id || null;
  const home_address_id =
    savedData?.customer_address_id || addressData?.customer_address_id || null;
  const service_offering_ids =
    serviceData?.newServices.map((s) => s.service_offering_id) || [];
  const totalLaborHours = serviceData?.newServices.reduce((total, service) => {
    return total + service.labor_hours;
  }, 0);
  const isShopService =
    serviceLocations?.includes('shop') && !!client_location_id;
  let locationType;
  if (!!home_address_id && !client_location_id) {
    locationType = 'home';
  } else if (isShopService) {
    locationType = 'shop';
  } else {
    locationType = 'workplace';
  }

  const handleSubmit = (values) => {
    if (isUsingTimeSlots) {
      setScheduleData((prev) => ({
        ...prev,
        ...values,
        planned_arrival_time: null,
        planned_departure_time: null,
        confirmed_appointment_start_time: selectedTimeSlot,
      }));
    } else {
      setScheduleData((prev) => ({
        ...prev,
        ...values,
        planned_arrival_time: arrivalTime,
        planned_departure_time: departureTime,
        confirmed_appointment_start_time: null,
      }));
    }
    handleNext();
  };

  useEffect(() => {
    dataProvider
      .getList('service-vehicle', {
        filter: {
          is_active: true,
        },
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 2000 },
      })
      .then(({ data }) => {
        let svList = data;
        // Filter the list to just service vehicles or bays
        if (isShopService) {
          svList = svList.filter(
            (sv) =>
              sv.is_service_bay &&
              sv.home_client_location_id === client_location_id,
          );
        } else {
          svList = svList.filter((sv) => !sv.is_service_bay);
        }
        setServiceVehicleList(svList);
        // Time Slots does not use the optimal service vehicle feature, so set an initial SV
        if (isUsingTimeSlots && svList.length > 0) {
          setScheduleData((prev) => ({
            ...prev,
            service_vehicle_id: svList[0].service_vehicle_id,
            service_vehicle_name: svList[0].service_vehicle_name,
          }));
        }
        // If there is only one active service vehicle, don't show optimal scheduling option
        if (svList.length <= 1) {
          setIsUsingOptimalSchedule(false);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    setScheduleData((prev) => ({
      ...prev,
      planned_arrival_time: arrivalTime,
      planned_departure_time: departureTime,
    }));
  }, [arrivalTime, departureTime]);

  const selectServiceVehicle = (event) => {
    const { value: serviceVehicleId } = event.target;
    setIsUsingOptimalSchedule(
      serviceVehicleId === 'auto' &&
        !isRecurring &&
        serviceVehicleList.length > 1,
    );
    if (serviceVehicleId !== 'auto') {
      const serviceVehicleName = serviceVehicleList.find(
        (sv) => sv.id === serviceVehicleId,
      )?.name;
      if (scheduleData) {
        setScheduleData((prev) => ({
          ...prev,
          service_vehicle_id: serviceVehicleId,
          service_vehicle_name: serviceVehicleName,
        }));
      }
    }
  };

  // recurringDatesWithConflicts holds a list of dates with conflicts, even if the user is not scheduling recurring appointments
  // We can use this list to identify if our selected date has a conflict
  // This is only necessary for availability windows (non-time slots). Time slots will calculate conflicts separately
  useEffect(() => {
    if (!isUsingTimeSlots) {
      const dateWithConflict = recurringDatesWithConflicts.find(
        (d) =>
          d.date === moment(scheduleData?.selected_date).format('YYYY-MM-DD'),
      );
      setConflictReason(
        dateWithConflict ? dateWithConflict.short_reason : null,
      );
    }
  }, [scheduleData?.selected_date, recurringDatesWithConflicts]);

  const renderSetScheduleLink = () => (
    <Typography variant="subtitle2" gutterBottom fontStyle="italic">
      No available dates found.{' '}
      <Link
        href="#/schedule/edit-availability"
        color="primary"
        style={{ textDecoration: 'underline' }}
      >
        <span>Click here to set a schedule.</span>
      </Link>
    </Typography>
  );

  const renderServiceVehicleDropdown = () => {
    if (loadingDates) return null;
    if (!scheduleData?.service_vehicle_id) {
      return renderSetScheduleLink();
    }
    return (
      <Select
        className="vehicle-select"
        size="large"
        value={
          isUsingOptimalSchedule && !isUsingTimeSlots
            ? 'auto'
            : scheduleData.service_vehicle_id
        }
        onChange={selectServiceVehicle}
      >
        {!isRecurring && !isUsingTimeSlots && (
          <MenuItem key="auto" value="auto" name="auto">
            <strong>Automatically Select Best Available</strong>
          </MenuItem>
        )}
        {serviceVehicleList.map((sv) => (
          <MenuItem
            key={sv.service_vehicle_id}
            value={sv.service_vehicle_id}
            name={sv.name}
          >
            {sv.name}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const renderServiceVehicleText = () => {
    if (loadingDates) return null;
    if (!scheduleData?.service_vehicle_id) {
      return renderSetScheduleLink();
    }
    return (
      <Typography variant="subtitle2" gutterBottom>
        {scheduleData.service_vehicle_name}
      </Typography>
    );
  };

  const renderAvailabilityWindowDropdowns = () => (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs>
        <TextInput
          source="planned_arrival_time"
          label="Arrival time"
          type="time"
          value={arrivalTime}
          onChange={(event) => setArrivalTime(event.target.value)}
          inputProps={{
            step: THIRTY_MINUTE_INTERVAL_IN_SECONDS,
          }}
        />
      </Grid>
      <Grid item xs>
        <TextInput
          source="planned_departure_time"
          label="Departure time"
          type="time"
          value={departureTime}
          onChange={(event) => setDepartureTime(event.target.value)}
          inputProps={{
            step: THIRTY_MINUTE_INTERVAL_IN_SECONDS,
          }}
        />
      </Grid>
    </Grid>
  );

  const isXSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const horizontalPadding = isUsingTimeSlots && isXSmall ? 0 : 'auto';

  return (
    <div className={classes.main}>
      <FormWithRedirect
        record={scheduleData}
        save={handleSubmit}
        render={({ handleSubmitWithRedirect, saving }) => (
          <Grid
            container
            item
            xs={12}
            spacing={3}
            className={`${classes.formRoot} ${classes.width100} ${classes.scheduleForm}`}
          >
            <Grid
              item
              xs={12}
              md={isUsingTimeSlots ? 8 : true}
              style={{
                paddingLeft: horizontalPadding,
                paddingRight: horizontalPadding,
              }}
            >
              <CompatibleDatesCalendar
                record={{
                  service_offering_ids,
                  client_location_id,
                  home_address_id,
                  total_labor_hours: totalLaborHours,
                  location_type: locationType,
                }}
                serviceVehicleList={serviceVehicleList}
              />
              {!isRecurring && conflictReason && (
                <Typography>
                  <WarningOutlineIcon className={classes.warningIcon} />
                  {moment(scheduleData?.selected_date).format('MMMM DD')} has a
                  conflict:{' '}
                  <strong style={{ color: '#D64747' }}>{conflictReason}</strong>
                </Typography>
              )}
              {!loadingDates && isOfferingRecurringAppointments && (
                <BooleanInput
                  label={
                    <>
                      Create Repeat Appointments
                      <LoopIcon fontSize="small" className={classes.loopIcon} />
                    </>
                  }
                  source="is_recurring"
                  defaultValue={isRecurring}
                  onChange={(val) => {
                    setScheduleData((prev) => ({
                      ...prev,
                      is_recurring: val,
                    }));
                    setIsRecurring(val);
                    // Optimal scheduling not available for recurring
                    setIsUsingOptimalSchedule(
                      !val && serviceVehicleList.length > 1,
                    );
                  }}
                />
              )}
              {isRecurring && (
                <Grid item xs={12}>
                  <Grid
                    container
                    style={{
                      display: 'flex',
                      backgroundColor: '#F5F3F3',
                      padding: '10px',
                    }}
                  >
                    <Grid item xs={12}>
                      <RecurringPlanForm
                        isQuickAppointment
                        rruleAttributes={rruleAttributes}
                        rruleCombined={rruleCombined}
                        onRruleChange={onRruleChange}
                        isEndDateInvalid={isEndDateInvalid}
                        selectedTimeSlot={selectedTimeSlot}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <RecurringPlanSummary
                        datesToCreate={datesToCreate}
                        setDatesToCreate={setDatesToCreate}
                        unavailableDates={unavailableRecurringDates}
                        datesWithConflicts={recurringDatesWithConflicts}
                        showConflictLoadingIndicator={isLoadingAdditionalDates}
                        isCompact
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Divider orientation="vertical" flexItem />
            <Grid item xs className={classes.p10}>
              <Box mt={2.7} ml={1} mb={1.8}>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  width="100%"
                >
                  {isShopService ? (
                    <BuildIcon fontSize="medium" color="primary" />
                  ) : (
                    <LocalShippingIcon fontSize="medium" color="primary" />
                  )}
                  <Box ml={1} width="100%">
                    <Typography variant="subtitle1">
                      {isShopService ? 'Service Bay' : 'Service Vehicle'}
                    </Typography>
                    {serviceVehicleList.length > 1
                      ? renderServiceVehicleDropdown()
                      : renderServiceVehicleText()}
                    {isUsingOptimalSchedule &&
                      scheduleData?.service_vehicle_id &&
                      scheduleData?.selected_date && (
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          style={{ fontStyle: 'italic', fontSize: '.9rem' }}
                        >
                          <>
                            Best available for{' '}
                            {moment(scheduleData?.selected_date).format(
                              'MMMM DD',
                            )}
                            :{' '}
                            <strong>
                              {scheduleData?.service_vehicle_name}
                            </strong>
                          </>
                        </Typography>
                      )}
                  </Box>
                </Box>
              </Box>

              <Box ml={1}>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  width="100%"
                >
                  {!isUsingTimeSlots && (
                    <>
                      <AccessTimeIcon fontSize="medium" color="primary" />
                      <Box ml={1}>
                        <Typography variant="subtitle1">
                          Customer Availability Range
                        </Typography>
                        {renderAvailabilityWindowDropdowns()}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>

              {!client_location_id && !isSkippingVehicle && (
                <Box ml={1}>
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    width="100%"
                  >
                    <VpnKeyIcon fontSize="medium" color="primary" />
                    <Box ml={1} width="100%">
                      <Typography variant="subtitle1">
                        Key Location (optional)
                      </Typography>
                      <TextInput
                        multiline
                        source="key_exchange_note"
                        placeholder="Please note where technician can find keys on day of service."
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              <Box ml={1}>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  width="100%"
                >
                  <NotesIcon fontSize="medium" color="primary" />
                  <Box ml={1} width="100%">
                    <Typography variant="subtitle1">
                      Admin Note (optional)
                    </Typography>
                    <TextInput
                      multiline
                      source="admin_note"
                      placeholder={
                        getVehicleType(vehicleData) === 'Automobile'
                          ? 'This will not be visible to the customer.'
                          : 'Include Important Details for the Technician. Ex: Airplane Hangar, Harbor Port, RV Lot Number, or garage details. Access to water & Power?'
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
            >
              <DialogActions>
                <SaveButton
                  label="Review Appointment"
                  handleSubmitWithRedirect={handleSubmitWithRedirect}
                  saving={saving}
                  disabled={
                    (!isSkippingVehicle && !completedSteps[2]) ||
                    !scheduleData?.selected_date ||
                    !scheduleData?.service_vehicle_id ||
                    isEndDateInvalid ||
                    (isRecurring && isLoadingAdditionalDates) ||
                    (isUsingTimeSlots && !selectedTimeSlot)
                  }
                  className={classes.buttonWithoutIcon}
                />
              </DialogActions>
            </Box>
          </Grid>
        )}
      />
    </div>
  );
};

export default ScheduleForm;
